/**
 * Cache-aware decay framework — configuration layer (Layer 1 of 3).
 *
 * Data-only module. Defines the typed shapes for global cache schedule
 * tuning and per-tool overrides, plus the `DEFAULT_CACHE_SCHEDULE`
 * shipped in v1 and a `mergeSchedule` helper that fuses a tool's
 * partial override onto the global defaults.
 *
 * No logic lives here — every decision function in `decay-helpers.ts`
 * takes a fully-resolved {@link CacheScheduleConfig}. To retune the
 * framework's behavior, edit values in this file. See ADR-052.
 */

/**
 * One tier in a decay schedule. Tiers are ordered ASC by `threshold`;
 * the highest-threshold tier whose `threshold <= value` activates.
 */
export interface DecayTier {
  /** Multiplier applied to the per-tool `baseline` to get the size cap. */
  ratio: number;
  /** Inclusive lower bound — `value >= threshold` activates this tier. */
  threshold: number;
}

/**
 * Global cache-aware decay schedule. All values are exposed as knobs
 * so the framework's behavior can be retuned from one place.
 *
 * The two axes — `turnDelta` and `inTurnStepCount` — mirror each other:
 * "more elapsed (steps × turns) = tighter threshold, at exponentially
 * spaced check-in points".
 */
export interface CacheScheduleConfig {
  /**
   * In-turn step counts that trigger a sweep tick. Between ticks the
   * formatter is a no-op (cache stable). Defaults to powers of 2 >= 4
   * so cache breaks are O(log N) per N-step turn.
   */
  inTurnSweepTicks: readonly number[];
  /**
   * Sorted ASC by threshold. `inTurnStepCount >= threshold` activates
   * the tier. Only consulted on sweep-tick steps.
   */
  inTurnTickTiers: readonly DecayTier[];
  /**
   * Sorted ASC by threshold. `contextFillRatio >= threshold` activates
   * the tier. A pressure tier trims any item past the stale floor on
   * every step, not just sweep ticks, and tightens the turn tiers when
   * its ratio is smaller — a near-full prompt is worth a cache break
   * near its tail.
   */
  pressureTiers: readonly DecayTier[];
  /**
   * Minimum `stepDelta` for in-turn sweep eligibility. Items more
   * recent than this are never trimmed mid-turn (always recent enough
   * to be in active use). Turn-tier ignores this — turn transitions
   * always trim regardless of `stepDelta`.
   */
  staleFloorStepDelta: number;
  /** Sorted ASC by threshold. `turnDelta >= threshold` activates the tier. */
  turnDeltaTiers: readonly DecayTier[];
}

/**
 * Per-tool decay config. Drives the {@link CacheScheduleConfig}
 * ratios against a tool-specific `baseline`, plus a hard `minTrimSize`
 * floor that prevents trimming payloads too small to matter.
 *
 * A tool that wants a totally different schedule shape sets `schedule`
 * to a partial override; `mergeSchedule` shallow-merges it onto
 * {@link DEFAULT_CACHE_SCHEDULE}.
 */
export interface ToolDecayConfig {
  /** Char-length anchor; all tier `ratio` values multiply against this. */
  baseline: number;
  /**
   * Hard floor — never trim payloads smaller than this, regardless of
   * tier. Prevents thrashing on tiny payloads where the reference
   * shape would be larger than the value itself.
   */
  minTrimSize: number;
  /**
   * Preview head bytes kept in the truncation reference. Defaults to
   * `DEFAULT_TOOL_RESULT_PREVIEW_CHARS` (20 KB).
   */
  previewChars?: number;
  /**
   * Optional partial override of the global cache schedule for this
   * tool. Use sparingly — most tools should rely on the globally-tuned
   * shape and only set `baseline` / `minTrimSize`.
   */
  schedule?: Partial<CacheScheduleConfig>;
}

/**
 * V1 cache-aware decay schedule.
 *
 * **Sweep ticks**: log-spaced (powers of 2 >= 4). Steps 1, 2, 3, 5, 6,
 * 7, 9-15, ... are pure cache hits. Bounds within-turn cache breaks
 * to O(log N).
 *
 * **Turn tiers**: bulk-trim at user-message transitions. Ratios 0.50
 * / 0.25 / 0.10 mirror the in-turn tier ratios at depths 16 / 32 / 64
 * so the model is "more steps in this turn behaves like more turns
 * passing".
 *
 * **In-turn tick tiers**: gentler interpolation (ticks 4 / 8) catches
 * large items earlier when the turn is still short; deeper ticks
 * (16+) match the turn-tier ratios.
 *
 * **Pressure tiers**: at 70% / 85% of the compaction window, items past
 * the stale floor are held to the 32- / 64-step ratios immediately
 * instead of waiting for the turn to get that deep.
 *
 * **Stale-floor 2**: items 0 or 1 steps back are always considered
 * "active use" and skipped by mid-turn sweeps.
 */
export const DEFAULT_CACHE_SCHEDULE: CacheScheduleConfig = {
  inTurnSweepTicks: [4, 8, 16, 32, 64, 128, 256],
  turnDeltaTiers: [
    { threshold: 1, ratio: 0.5 },
    { threshold: 2, ratio: 0.25 },
    { threshold: 3, ratio: 0.1 },
  ],
  inTurnTickTiers: [
    { threshold: 4, ratio: 1.0 },
    { threshold: 8, ratio: 0.75 },
    { threshold: 16, ratio: 0.5 },
    { threshold: 32, ratio: 0.25 },
    { threshold: 64, ratio: 0.1 },
  ],
  pressureTiers: [
    { threshold: 0.7, ratio: 0.25 },
    { threshold: 0.85, ratio: 0.1 },
  ],
  staleFloorStepDelta: 2,
};

/**
 * Prompt-cache lifetime assumed by the cold-cache sweep. Anthropic's
 * default (automatic) cache entry lives 5 minutes; past that gap a turn
 * re-writes its whole prefix anyway, so trimming prior turns is free.
 */
export const PROMPT_CACHE_TTL_MS = 5 * 60_000;

/**
 * Recommended default per-tool `baseline` (the size anchor that
 * tier `ratio` multipliers apply against to derive each tier's cap).
 *
 * Simulator-derived (`pnpm sim:decay`, ADR-053) — the value at which
 * trim coverage drops sharply with negligible cache-cost penalty
 * across the typical (median=10, mean=15) workload curve. Tools with
 * unusually small or large payload distributions can override
 * (e.g. `previewFile` base64 outputs warrant a much smaller baseline
 * since the output should always be replaced after the current step).
 */
export const DEFAULT_TOOL_BASELINE_CHARS = 40_000;

/**
 * Recommended default per-tool `minTrimSize` — the hard floor below
 * which payloads are never trimmed regardless of tier. Items in the
 * 2 KB - 5 KB band are exactly the ones where trimming barely
 * shrinks the prompt (the truncation reference is itself a few hundred
 * chars) but still costs a cache break — skipping them is
 * essentially free. Set to 5 KB based on the simulator sweep.
 */
export const DEFAULT_TOOL_MIN_TRIM_SIZE = 5000;

// ────────────────────────────────────────────────────────────────────
// Absolute single-payload hard cap
// ────────────────────────────────────────────────────────────────────

/** Pessimistic chars/token to convert the token window into a char budget (mirrors prompts/document-parts). */
const TOOL_OUTPUT_HARD_CAP_CHARS_PER_TOKEN = 3;

/** Max share of the context window a single tool payload may occupy before the hard cap fires. */
const TOOL_OUTPUT_HARD_CAP_RATIO = 0.25;

/**
 * Absolute char ceiling on {@link getToolOutputHardCapChars}, independent
 * of the model's context window. Also the cap used when the window is
 * unknown (<= 0).
 *
 * The window-relative cap alone let a 1M-token model carry ~750 KB per
 * tool result verbatim (~200K tokens each, several per turn, held for at
 * least two steps by the stale-floor and until the next sweep tick). The
 * costs that motivate a ceiling do not scale with the window: the raw
 * payload sits in the prompt for every step until it decays (tokens,
 * latency, cache churn), and every step re-serializes and ships it in
 * the model request — the 2026-09-28 turn that ran three ~1 MB `sed`
 * outputs through an 8-step turn at 568K input tokens per step
 * coincided with a cluster of heap-OOM replica crashes
 * (docs/incidents/2026-07-06-incomplete-turn.md). The persisted copy is
 * still `bound-tool-result.ts`'s 1 MB concern; this ceiling only bounds
 * what the model sees per step. Above this size the
 * model gets the 20 KB preview plus the `recallToolResult` pointer on the
 * landing step and recalls the slice it needs; that is
 * already how tools tell the model to approach large outputs.
 *
 * 200K chars is ~50-65K tokens, still 5x the default decay baseline.
 */
export const TOOL_OUTPUT_HARD_CAP_MAX_CHARS = 200_000;

/**
 * Absolute char ceiling for a SINGLE tool payload, given the model's
 * context window. Unlike the cache-aware tiers this is not a
 * cache-friendly deferred trim: a payload over this ceiling is trimmed
 * immediately, on the step it lands, regardless of the sweep/turn
 * schedule and the stale-floor (see {@link shouldTrimByCacheSchedule}).
 * Guards the "single tool blows up context" case — one `cat huge.json`
 * or runaway SQL result can't push the prompt over the window before
 * decay has a chance to act.
 *
 * Mirrors `getFreshTurnDocumentBudget` in scaling with the context
 * window ({@link TOOL_OUTPUT_HARD_CAP_RATIO} of the window, in chars),
 * clamped to {@link TOOL_OUTPUT_HARD_CAP_MAX_CHARS}. Small-window models
 * get the proportional cap (200K tokens → 150K chars); windows of
 * ~267K tokens and above hit the ceiling. `contextWindow <= 0` (window
 * unknown) returns the ceiling.
 */
export function getToolOutputHardCapChars(contextWindow: number): number {
  if (contextWindow <= 0) {
    return TOOL_OUTPUT_HARD_CAP_MAX_CHARS;
  }
  const windowChars = contextWindow * TOOL_OUTPUT_HARD_CAP_CHARS_PER_TOKEN;
  return Math.min(
    TOOL_OUTPUT_HARD_CAP_MAX_CHARS,
    Math.floor(windowChars * TOOL_OUTPUT_HARD_CAP_RATIO)
  );
}

/**
 * Recommended starting {@link ToolDecayConfig} for tools that don't
 * have unusual payload-size distributions. Most tools should spread
 * this and override only what they need:
 *
 * ```ts
 * const BASH_CFG: ToolDecayConfig = {
 *   ...DEFAULT_TOOL_DECAY_CONFIG,
 *   // optionally tighten `minTrimSize` for tools whose outputs are
 *   // routinely > 100 KB and we'd rather trim them eagerly.
 * };
 * ```
 */
export const DEFAULT_TOOL_DECAY_CONFIG: ToolDecayConfig = {
  baseline: DEFAULT_TOOL_BASELINE_CHARS,
  minTrimSize: DEFAULT_TOOL_MIN_TRIM_SIZE,
};

// Document and image decay no longer plug into the cache-aware
// framework — they share the simpler rule-based decay in
// `prompts/file-decay.ts` (ADR-055 §7). The framework continues to
// govern tool results.

/**
 * Shallow-merges a per-tool {@link ToolDecayConfig.schedule} override
 * onto {@link DEFAULT_CACHE_SCHEDULE}. Fields the tool omits fall
 * through to the global defaults.
 *
 * Pure — does not mutate either input.
 */
export function mergeSchedule(
  toolConfig: ToolDecayConfig
): CacheScheduleConfig {
  const override = toolConfig.schedule;
  if (!override) {
    return DEFAULT_CACHE_SCHEDULE;
  }
  return {
    inTurnSweepTicks:
      override.inTurnSweepTicks ?? DEFAULT_CACHE_SCHEDULE.inTurnSweepTicks,
    turnDeltaTiers:
      override.turnDeltaTiers ?? DEFAULT_CACHE_SCHEDULE.turnDeltaTiers,
    inTurnTickTiers:
      override.inTurnTickTiers ?? DEFAULT_CACHE_SCHEDULE.inTurnTickTiers,
    pressureTiers:
      override.pressureTiers ?? DEFAULT_CACHE_SCHEDULE.pressureTiers,
    staleFloorStepDelta:
      override.staleFloorStepDelta ??
      DEFAULT_CACHE_SCHEDULE.staleFloorStepDelta,
  };
}

/**
 * Decay for results that inject an image (`previewFile`, `knowledgeGetMedia`):
 * live within the creating turn, collapsed at the first sweep tick or turn
 * transition. The output carries a URL rather than bytes, so the formatter's
 * only job is to mark the result stale so the injection pass stops re-sending
 * the picture — see `makeVisionResultDecayFormatter`.
 */
export const VISION_RESULT_DECAY_CFG: ToolDecayConfig = {
  baseline: 5000,
  minTrimSize: 0,
  previewChars: 0,
};
